import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/reducers/rootReducer";
import BundleKey from "../../../../../translation/bundleKey";
import AccordionCard from "../../../../../components/common/PL/AccordionCard/AccordionCard";
import ContractorContentForm from "./ContractorContentForm/ContractorContentForm";
import EventualContentForm from "./EventualContentForm/EventualContentForm";
import TransportContentForm from "./TransportContentForm/TransportContentForm";
import VisitorContentForm from "./VisitorContentForm/VisitorContentForm";
import AccessTypeMethodCard from "../../../../../components/common/PL/Cards/AccessTypeMethodCard/AccessTypeMethodCard";
import AccessTypeResultCard from "../../../../../components/common/PL/Cards/AccessTypeResultCard/AccessTypeResultCard";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";
import "./AccessTypeForm.css";
import { ACCESS_METHOD_OPTIONS } from "../../utils/accessMethodItems";
import { isTransportCompanionEvent } from "../../utils/guestTypeUtils";
import {
  buildTransportInheritedConfirmation,
  INHERITED_FROM_TRANSPORT,
} from "../../utils/transportInheritedConfirmation";
import { EntryRegistrationFormValues } from "../types";
import { RegistrationMode } from "../../registroIngresoNavigation";
import { TransportAuthorizationFlow } from "./TransportContentForm/useTransportAuthorizationFlow";

import { useRegistroIngresoSession } from "../../session/RegistroIngresoSessionContext";

// Del acompañante de un transportista sólo se sabe que viajó con el chofer: o egresa como
// transportista o egresa como visitante. Contratista y eventual necesitan una prevalidación propia
// que no tiene.
const TRANSPORT_COMPANION_METHODS = ["visitor", "transport"];

type AccessTypeFormProps = {
  formId: string;
  mode: RegistrationMode;
  authorizations?: any[];
  /** Sólo se usa (y sólo se pasa) cuando `selectedAccessMethod === "transport"`: ver
   * `useTransportAuthorizationFlow`, instanciado una única vez en `EntryRegistrationForm` y
   * compartido con el paso "Autorizaciones". */
  transportFlow?: TransportAuthorizationFlow;
};

const AccessTypeForm = ({
  formId,
  mode,
  authorizations: propsAuthorizations,
  transportFlow,
}: AccessTypeFormProps) => {
  const { tabs, refetchAuthorizations } = useRegistroIngresoSession();
  const currentTab = tabs.find((t) => t.id === formId);
  const authorizations = propsAuthorizations ?? currentTab?.authorizations;
  const {
    watch,
    getValues,
    setValue,
    register,
    unregister,
    formState: { errors, isSubmitted },
  } = useFormContext<EntryRegistrationFormValues>();
  const selectedAccessMethod = watch("selectedAccessMethod");
  const documentNumber = watch("documentNumber");
  const documentCountry = watch("documentCountry");
  const isEmployee = watch("isEmployee");
  const entranceEvent = watch("entranceEvent");
  const employeeWorkplaces = watch("employeeWorkplaces") || [];
  const allCompanies = useSelector((state: RootState) => state.auth.companies);
  const allowTransport = useSelector((state: RootState) => state.auth.allowTransport);

  // "Transporte" sólo se ofrece como tipo de autorización si el edificio lo tiene habilitado.
  const accessMethodOptions = allowTransport
    ? ACCESS_METHOD_OPTIONS
    : ACCESS_METHOD_OPTIONS.filter((option) => option.type !== "transport");

  const titleKey =
    mode === "exit" ? BundleKey.ACCESS_TYPE_FORM_TITLE_EXIT : BundleKey.ACCESS_TYPE_FORM_TITLE;

  // Register selectedAccessMethod as required (skip for employees)
  useEffect(() => {
    if (!isEmployee) {
      register("selectedAccessMethod", { required: "El tipo de autorización es obligatorio" });
      return () => {
        unregister("selectedAccessMethod");
      };
    }
  }, [register, unregister, isEmployee]);

  // Keep validation in sync
  useEffect(() => {
    if (!isEmployee) {
      setValue("selectedAccessMethod", selectedAccessMethod || ("" as any), {
        shouldValidate: isSubmitted,
      });
    }
  }, [selectedAccessMethod, setValue, isSubmitted, isEmployee]);

  const hasAccessMethodError = !isEmployee && isSubmitted && !!errors.selectedAccessMethod;

  /**
   * Un chofer de transporte que sale a pie y se egresa como visita/contratista/eventual hereda la
   * empresa y el anfitrión de la autorización con la que entró, como confirmación en guardia
   * preseleccionada. Al volver a "Transporte" (o a cualquier otro tipo que no sea uno de esos) se
   * retira: el egreso de transporte viaja con su propia autorización y no debe llevar la copia.
   */
  const syncInheritedTransportConfirmation = (methodType: string) => {
    if (mode !== "exit") return;

    const currentData: any[] = getValues("employeeConfirmationsData") || [];
    const inherited = currentData.filter((item) => item?.[INHERITED_FROM_TRANSPORT]);
    const current = getValues("employeeConfirmations") || [];

    const isInheritedConfirmation = (c: { host: string | null; company: string }) =>
      inherited.some((item) => (item.host?.id || item.host) === c.host && item.company === c.company);
    const base = current.filter((c) => !isInheritedConfirmation(c));
    const baseData = currentData.filter((item) => !item?.[INHERITED_FROM_TRANSPORT]);

    const inheritsToMethod = ["visitor", "contractor", "eventual"].includes(methodType);
    const seed = inheritsToMethod ? buildTransportInheritedConfirmation(entranceEvent) : null;
    const alreadyThere =
      !!seed &&
      base.some((c) => c.host === seed.confirmation.host && c.company === seed.confirmation.company);

    if (!seed && inherited.length === 0) return;

    setValue("employeeConfirmations", seed && !alreadyThere ? [...base, seed.confirmation] : base);
    setValue(
      "employeeConfirmationsData",
      seed && !alreadyThere ? [...baseData, seed.data] : baseData
    );
    if (seed && !alreadyThere) setValue("authorizationCompany", seed.confirmation.company);
  };

  const handleAccessMethodClick = (methodType: string) => {
    if (selectedAccessMethod !== methodType) {
      setValue("selectedAccessMethod", methodType as any, { shouldValidate: isSubmitted });
      syncInheritedTransportConfirmation(methodType);
      if (documentNumber && documentCountry) {
        refetchAuthorizations(formId, methodType, documentNumber, documentCountry);
      }
    }
  };

  const renderSelectedAccessContent = () => {
    switch (selectedAccessMethod) {
      case "contractor":
        return (
          <ContractorContentForm formId={formId} authorizations={authorizations} disabled={false} />
        );
      case "eventual":
        return (
          <EventualContentForm formId={formId} authorizations={authorizations} disabled={false} />
        );
      case "visitor":
        return (
          <VisitorContentForm formId={formId} authorizations={authorizations} disabled={false} />
        );
      case "transport":
        return transportFlow ? (
          <TransportContentForm
            disabled={false}
            mode={mode}
            vehicles={currentTab?.vehicles ?? []}
            flow={transportFlow}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <AccordionCard id={`${formId}-access-type`} titleKey={titleKey} number={2} expanded>
      <div className="accessTypeFormContainer">
        {isEmployee ? (
          <div className="employeeCardsContainer">
            {employeeWorkplaces.map((wp: any, index: number) => {
              const companyObj = allCompanies?.find((c: any) => c.id === wp.company);
              const companyName =
                companyObj?.name || companyObj?.description || wp.company || "N/A";

              let managerName = "N/A";
              if (wp.manager) {
                if (typeof wp.manager === "object") {
                  managerName =
                    `${wp.manager.firstname || ""} ${wp.manager.lastname || ""}`.trim() || "N/A";
                } else {
                  managerName = wp.managerName || wp.manager;
                }
              }

              return (
                <AccessTypeResultCard
                  key={index}
                  label="Empleado"
                  variant="employee"
                  showCheckbox={false}
                  leftIcon={<AssignmentIndIcon />}
                  managerLabel="Manager"
                  managerName={managerName}
                  phoneLabel="Empresa"
                  managerPhone={companyName}
                />
              );
            })}
          </div>
        ) : (
          <>
            <div className="accessTypeMethodGrid" data-field-name="selectedAccessMethod">
              {accessMethodOptions.map((option) => (
                <AccessTypeMethodCard
                  key={option.type}
                  labelKey={option.labelKey}
                  icon={React.createElement(option.icon, { fontSize: "inherit" })}
                  selected={selectedAccessMethod === option.type}
                  onClick={() => handleAccessMethodClick(option.type)}
                  hasError={hasAccessMethodError}
                />
              ))}
            </div>
            {hasAccessMethodError && (
              <span className="accessTypeMethodError">
                Debe seleccionar un tipo de autorización
              </span>
            )}

            {renderSelectedAccessContent()}
          </>
        )}
      </div>
    </AccordionCard>
  );
};

export default AccessTypeForm;
